import deepmerge from 'deepmerge'

import * as ModelTypes from '@models/types'

import type { ModuleState } from '@redux/modules/types'

const DELETE_ENTITY = 'realbase/entity/DELETE_ENTITY'
const REPLACE_ENTITY = 'realbase/entity/REPLACE_ENTITY'
const UPDATE_ENTITY = 'realbase/entity/UPDATE_ENTITY'

// Add Entities & their Model Type Reference here
export type EntitiesState = {
// START ENTITY TYPES
  _updates: {},
  activities: { [key: number]: ModelTypes.ActivityModel },
  agencies: { [key: number]: ModelTypes.AgencyModel },
  // @ts-expect-error TS2724
  artworkPages: { [key: number]: ModelTypes.ArtworkPageModel },
  artworks: { [key: number]: ModelTypes.ArtworkModel },
  attachments: { [key: number]: ModelTypes.AttachmentModel },
  brandThemes: { [key: number]: ModelTypes.BrandThemeModel },
  brands: { [key: number]: ModelTypes.BrandModel },
  campaigns: { [key: number]: ModelTypes.CampaignModel },
  chartData: { [key: number]: {} },
  comments: { [key: number]: ModelTypes.CommentModel },
  conversations: { [key: number]: ModelTypes.ConversationModel },
  currentUser: { [key: number]: ModelTypes.UserModel },
  // @ts-expect-error TS2694
  images: { [key: number]: ModelTypes.ImageModel }, // example
  layoutTemplates: { [key: number]: ModelTypes.LayoutTemplateModel },
  orderItems: { [key: number]: ModelTypes.OrderItemModel },
  orders: { [key: number]: ModelTypes.OrderModel },
  providers: { [key: number]: ModelTypes.ProviderModel },
  publications: { [key: number]: ModelTypes.PublicationModel },
  publicationProducts: { [key: number]: object },
  relatedEntities: { [key: number]: ModelTypes.RelatedEntityModel },
  roles: { [key: number]: ModelTypes.RoleModel },
  serviceProducts: { [key: number]: ModelTypes.ServiceProductModel },
  services: { [key: number]: ModelTypes.ServiceModel },
  // @ts-expect-error TS2694
  statuses: { [key: number]: ModelTypes.StatusModel },
  subscribers: { [key: number]: ModelTypes.SubscriberModel },
  taskListItems: { [key: number]: ModelTypes.TaskListItemModel },
  taskListSections: { [key: number]: ModelTypes.TaskListSectionModel },
  taskListTemplateItems: { [key: number]: ModelTypes.TaskListTemplateItemModel },
  taskListTemplateSections: { [key: number]: ModelTypes.TaskListTemplateSectionModel },
  taskListTemplates: { [key: number]: ModelTypes.TaskListTemplateModel },
  taskLists: { [key: number]: ModelTypes.TaskListModel },
  tags: { [key: number]: ModelTypes.TagModel },
  teamMembers: { [key: number]: ModelTypes.TeamMemberModel },
  teams: { [key: number]: ModelTypes.TeamModel },
  templateGroups: { [key: number]: ModelTypes.TemplateGroupModel },
  templatePagePartials: { [key: number]: ModelTypes.TemplatePagePartialModel },
  templatePages: { [key: number]: ModelTypes.TemplatePageModel },
  trackingLinks: { [key: number]: ModelTypes.TrackingLinkModel },
  users: { [key: number]: ModelTypes.UserModel },
// END ENTITY TYPES
}

const initialState: EntitiesState = {
// START ENTITIES STATE
  _updates: {},
  activities: {},
  agencies: {},
  artworkPages: {},
  artworks: {},
  attachments: {},
  brandThemes: {},
  brands: {},
  campaigns: {},
  chartData: {},
  comments: {},
  conversations: {},
  currentUser: {},
  images: {},
  layoutTemplates: {},
  orderItems: {},
  orders: {},
  providers: {},
  publications: {},
  publicationProducts: {},
  relatedEntities: {},
  roles: {},
  serviceProducts: {},
  services: {},
  statuses: {},
  subscribers: {},
  taskListItems: {},
  taskListSections: {},
  taskListTemplateItems: {},
  taskListTemplateSections: {},
  taskListTemplates: {},
  taskLists: {},
  teamMembers: {},
  teams: {},
  tags: {},
  templateGroups: {},
  templatePagePartials: {},
  templatePages: {},
  trackingLinks: {},
  users: {},
// END ENTITIES STATE
}

// Normalized Data
type EntitiesActionFunctionPayload = {
  entities: Partial<EntitiesState>,
}

type ReducerResult = EntitiesActionFunctionPayload['entities'] & {
  type: string,
}

type Action = Partial<EntitiesState> & Partial<ModuleState> & {
  type?: string,
}

export function updateEntities(payload: EntitiesActionFunctionPayload): ReducerResult {
  return { type: UPDATE_ENTITY, ...payload.entities }
}

export function replaceEntity(payload: EntitiesActionFunctionPayload): ReducerResult {
  return { type: REPLACE_ENTITY, ...payload.entities }
}

export function deleteEntity(payload: EntitiesActionFunctionPayload): ReducerResult {
  return { type: DELETE_ENTITY, ...payload.entities }
}

function cleanKeys(
  keys: (keyof Action)[] = [],
  removeKeys: (keyof Action)[] = [],
): (keyof EntitiesState)[] {
  removeKeys.forEach((removeKey) => {
    const index = keys.indexOf(removeKey)
    keys.splice(index, 1)
  })

  // @ts-expect-error TS2322
  return keys
}

// Reducers
function addEntities(state: EntitiesState, action: Action): EntitiesState {
  // @ts-expect-error TS2345
  const keys = cleanKeys(Object.keys(action), ['type'])
  const newState = { ...state }

  // Don't merge arrays
  // @ts-expect-error TS7006
  const dontMerge = (_, source) => source
  const mergeOptions = { arrayMerge: dontMerge }

  keys.forEach((key) => {
    // @ts-expect-error TS2769
    newState[key] = deepmerge(newState[key], action[key], mergeOptions)
  })

  return newState
}

function replaceEntities(state: EntitiesState, action: Action): EntitiesState {
  // @ts-expect-error TS2345
  const keys = cleanKeys(Object.keys(action), ['type'])
  const newState = { ...state }

  keys.forEach((key) => {
    if (newState[key]){
      // @ts-expect-error TS2322
      newState[key] = { ...state[key] }

      // Keys of the item we need to replace
      // @ts-expect-error TS2769
      const itemKeys = Object.keys(action[key])
      itemKeys.forEach((itemKey) => {
        // @ts-expect-error TS7053
        newState[key][itemKey] = action[key][itemKey]
      })
    }
  })

  return newState
}

function removeEntities(state: EntitiesState, action: Action): EntitiesState {
  // @ts-expect-error TS2345
  const keys: (keyof EntitiesState)[] = cleanKeys(Object.keys(action), ['type'])
  const newState = { ...state }

  keys.forEach((key) => {
    if (newState[key]){
      // @ts-expect-error TS2322
      newState[key] = { ...state[key] }

      // Keys of the item we need to remove
      // @ts-expect-error TS2769
      const itemKeys = Object.keys(action[key])
      itemKeys.forEach((itemKey) => {
        // @ts-expect-error TS7053
        delete newState[key][itemKey]
      })
    }
  })

  return newState
}

export default function reducer(state: EntitiesState = initialState, action: Action = {}) {
  switch (action.type){
    case UPDATE_ENTITY:
      return addEntities(state, action)
    case REPLACE_ENTITY:
      return replaceEntities(state, action)
    case DELETE_ENTITY:
      return removeEntities(state, action)
    default:
      return state
  }
}
